/**
 * Shared Chart.js + heatmap panel for WebDAV / Drive statistics Preact pages.
 */

import { h } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { createStatsCharts, destroyStatsCharts } from '../../modules/drive/stats-charts.js'

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]

export function StatsChartsPanel({ stats, chartI18n }) {
  const trafficRef = useRef(null)
  const extensionsRef = useRef(null)
  const sizesRef = useRef(null)
  const chartsRef = useRef([])

  const traffic = stats?.daily_traffic || { labels: [], uploads: [], downloads: [] }
  const extensions = stats?.by_extension || {}
  const sizes = stats?.size_distribution || {}
  const heatmap = stats?.heatmap || []
  const i18n = chartI18n || {}

  const trafficEmpty =
    !(traffic.uploads || []).some((n) => n > 0) && !(traffic.downloads || []).some((n) => n > 0)
  const extensionsEmpty = Object.keys(extensions).length === 0
  const sizesEmpty = Object.values(sizes).every((n) => !n)
  const heatmapEmpty = !heatmap.some((cell) => cell.count > 0)

  useEffect(() => {
    destroyStatsCharts(chartsRef.current)
    chartsRef.current = createStatsCharts({
      trafficCanvas: trafficEmpty ? null : trafficRef.current,
      extensionsCanvas: extensionsEmpty ? null : extensionsRef.current,
      sizesCanvas: sizesEmpty ? null : sizesRef.current,
      trafficLabels: traffic.labels || [],
      trafficUploads: traffic.uploads || [],
      trafficDownloads: traffic.downloads || [],
      uploadsLabel: i18n.uploads || 'Uploads',
      downloadsLabel: i18n.downloads || 'Downloads',
      extensionLabels: Object.keys(extensions),
      extensionCounts: Object.values(extensions),
      sizeLabels: Object.keys(sizes),
      sizeCounts: Object.values(sizes),
      fileCountLabel: i18n.file_count || 'Files'
    })
    return () => {
      destroyStatsCharts(chartsRef.current)
      chartsRef.current = []
    }
  }, [stats, chartI18n])

  const maxCount = Math.max(1, ...heatmap.map((c) => c.count || 0))
  const countAt = (dow, hour) => {
    const cell = heatmap.find((c) => c.dow === dow && c.hour === hour)
    return cell?.count || 0
  }
  const dayLabels = i18n.day_labels || {}
  const dayKeys = i18n.day_keys || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

  return (
    <>
      <div class="row mb-4 webdav-stats-charts">
        <div class="col-lg-6 mb-4 mb-lg-0">
          <div class="card h-100">
            <div class="card-header">{i18n.traffic}</div>
            <div class="card-body">
              {trafficEmpty ? (
                <span class="text-muted">{i18n.no_events}</span>
              ) : (
                <div style="height: 260px; position: relative;">
                  <canvas ref={trafficRef} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 mb-4 mb-lg-0">
          <div class="card h-100">
            <div class="card-header">{i18n.file_types}</div>
            <div class="card-body">
              {extensionsEmpty ? (
                <span class="text-muted">{i18n.no_events}</span>
              ) : (
                <div style="height: 260px; position: relative;">
                  <canvas ref={extensionsRef} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6">
          <div class="card h-100">
            <div class="card-header">{i18n.file_sizes}</div>
            <div class="card-body">
              {sizesEmpty ? (
                <span class="text-muted">{i18n.no_events}</span>
              ) : (
                <div style="height: 260px; position: relative;">
                  <canvas ref={sizesRef} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-4 webdav-stats-charts">
        <div class="card-header bg-transparent d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>{i18n.time_distribution}</span>
          <span class="small text-muted d-flex align-items-center gap-2">
            {i18n.legend_low}
            <span class="d-inline-flex gap-1">
              {[0.1, 0.3, 0.5, 0.7, 1.0].map((o) => (
                <span
                  key={o}
                  style={`display:inline-block; width:14px; height:14px; border-radius:2px; background:rgba(59,130,246,${o});`}
                />
              ))}
            </span>
            {i18n.legend_high}
          </span>
        </div>
        <div class="card-body webdav-heatmap-scroll p-0">
          {heatmapEmpty ? (
            <div class="p-3">
              <span class="text-muted">{i18n.no_events}</span>
            </div>
          ) : (
            <div class="px-3 pb-3 pt-2" style="min-width: 680px;">
              <table class="w-100" style="border-collapse: separate; border-spacing: 1px;">
                <thead>
                  <tr>
                    <td />
                    {Array.from({ length: 24 }, (_, hour) => (
                      <td key={hour} class="text-muted text-center" style="font-size: 0.65rem;">
                        {hour}
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAY_ORDER.map((dow, idx) => (
                    <tr key={dow}>
                      <td
                        class="text-muted pe-2"
                        style="font-size: 0.7rem; white-space: nowrap; padding: 2px 4px;"
                      >
                        {dayLabels[dayKeys[idx]] || dayKeys[idx]}
                      </td>
                      {Array.from({ length: 24 }, (_, hour) => {
                        const count = countAt(dow, hour)
                        const opacity = count === 0 ? 0 : Math.min(0.08 + (count / maxCount) * 0.92, 1)
                        const hourLabel = `${String(hour).padStart(2, '0')}:00`
                        const title = (i18n.tooltip_template || '%{count} @ %{hour}')
                          .replace('%{count}', String(count))
                          .replace('%{hour}', hourLabel)
                        return (
                          <td
                            key={hour}
                            title={title}
                            style={`height: 18px; border-radius: 3px; margin: 1px; background: rgba(59,130,246,${opacity.toFixed(2)}); cursor: default;`}
                          />
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
